import {
  Music, Palette, UtensilsCrossed, Church, Brush, Trophy, PartyPopper, Snowflake,
  Gamepad2, Briefcase,
  Car, Waves, Mountain, Compass, Train,
  PawPrint, Binoculars, Skull, Landmark,
  CircleDot, CircleAlert, TriangleAlert, OctagonAlert,
} from "lucide-react";
import type { FestivalCategory } from "@/types/festival";
import type { RouteCategory } from "@/types/route";

/** Shared badge config: icon + Tailwind color classes + label */
interface CategoryBadgeConfig {
  icon: typeof Music;
  color: string;
  label: string;
}

// ── Festival Categories ──

export const FESTIVAL_CATEGORY_CONFIG: Record<FestivalCategory, CategoryBadgeConfig> = {
  music: { icon: Music, color: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300", label: "Music" },
  cultural: { icon: Palette, color: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300", label: "Cultural" },
  food: { icon: UtensilsCrossed, color: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300", label: "Food" },
  religious: { icon: Church, color: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300", label: "Religious" },
  art: { icon: Brush, color: "bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300", label: "Art" },
  sports: { icon: Trophy, color: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300", label: "Sports" },
  carnival: { icon: PartyPopper, color: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300", label: "Carnival" },
  seasonal: { icon: Snowflake, color: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300", label: "Seasonal" },
  gaming: { icon: Gamepad2, color: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300", label: "Gaming" },
  conference: { icon: Briefcase, color: "bg-brand-navy/10 text-accent-app dark:bg-brand-navy/40 dark:text-accent-app", label: "Conference" },
  wildlife: { icon: PawPrint, color: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300", label: "Wildlife" },
  safari: { icon: Binoculars, color: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300", label: "Safari" },
  "winter-sports": { icon: Mountain, color: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300", label: "Winter Sports" },
  snow: { icon: Snowflake, color: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300", label: "Snow" },
  "dark-tourism": { icon: Skull, color: "bg-slate-100 text-slate-700 dark:bg-slate-900/40 dark:text-slate-300", label: "Dark Tourism" },
  memorial: { icon: Landmark, color: "bg-slate-100 text-slate-700 dark:bg-slate-900/40 dark:text-slate-300", label: "Memorial" },
};

/** Dot colors for calendar views (single class, used in festival-guide calendar) */
export const FESTIVAL_DOT_COLORS: Record<FestivalCategory, string> = {
  music: "bg-purple-500",
  cultural: "bg-blue-500",
  food: "bg-orange-500",
  religious: "bg-amber-500",
  art: "bg-pink-500",
  sports: "bg-green-500",
  carnival: "bg-red-500",
  seasonal: "bg-cyan-500",
  gaming: "bg-indigo-500",
  conference: "bg-brand-navy",
  wildlife: "bg-green-500",
  safari: "bg-yellow-500",
  "winter-sports": "bg-cyan-500",
  snow: "bg-blue-500",
  "dark-tourism": "bg-slate-600",
  memorial: "bg-slate-500",
};

/** Dot colors with dark mode variants (used in TravelCalendar yearly planner) */
export const FESTIVAL_DOT_COLORS_DARK: Record<string, { bg: string; bgDark: string }> = {
  music:    { bg: "bg-purple-400", bgDark: "dark:bg-purple-600" },
  cultural: { bg: "bg-blue-400", bgDark: "dark:bg-blue-600" },
  food:     { bg: "bg-orange-400", bgDark: "dark:bg-orange-600" },
  religious:{ bg: "bg-amber-400", bgDark: "dark:bg-amber-600" },
  art:      { bg: "bg-pink-400", bgDark: "dark:bg-pink-600" },
  sports:   { bg: "bg-green-400", bgDark: "dark:bg-green-600" },
  carnival: { bg: "bg-red-400", bgDark: "dark:bg-red-600" },
  seasonal: { bg: "bg-cyan-400", bgDark: "dark:bg-cyan-600" },
  gaming:   { bg: "bg-indigo-400", bgDark: "dark:bg-indigo-600" },
  conference: { bg: "bg-brand-navy", bgDark: "dark:bg-brand-navy/80" },
  wildlife: { bg: "bg-green-400", bgDark: "dark:bg-green-600" },
  safari:   { bg: "bg-yellow-400", bgDark: "dark:bg-yellow-600" },
  "winter-sports": { bg: "bg-cyan-400", bgDark: "dark:bg-cyan-600" },
  snow:     { bg: "bg-blue-400", bgDark: "dark:bg-blue-600" },
  "dark-tourism": { bg: "bg-slate-500", bgDark: "dark:bg-slate-600" },
  memorial: { bg: "bg-slate-400", bgDark: "dark:bg-slate-600" },
};

/** Helper: get dot class string for a category (with dark mode) */
export function getFestivalDotClass(cat: string): string {
  const c = FESTIVAL_DOT_COLORS_DARK[cat];
  return c ? `${c.bg} ${c.bgDark}` : "bg-blue-400 dark:bg-blue-600";
}

// ── Route Categories ──

export const ROUTE_CATEGORY_CONFIG: Record<RouteCategory, CategoryBadgeConfig> = {
  pilgrimage: { icon: Church, color: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300", label: "Pilgrimage" },
  "scenic-drive": { icon: Car, color: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300", label: "Scenic Drive" },
  coastal: { icon: Waves, color: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300", label: "Coastal" },
  hiking: { icon: Mountain, color: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300", label: "Hiking" },
  "cultural-route": { icon: Palette, color: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300", label: "Cultural Route" },
  "food-trail": { icon: UtensilsCrossed, color: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300", label: "Food Trail" },
  adventure: { icon: Compass, color: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300", label: "Adventure" },
  "rail-journey": { icon: Train, color: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300", label: "Rail Journey" },
  conference: { icon: Briefcase, color: "bg-slate-100 text-slate-700 dark:bg-slate-900/40 dark:text-slate-300", label: "Business" },
  business: { icon: Briefcase, color: "bg-slate-100 text-slate-700 dark:bg-slate-900/40 dark:text-slate-300", label: "Business" },
};

export const ROUTE_DIFFICULTY_CONFIG: Record<string, { color: string; label: string; icon: typeof Music }> = {
  easy: { color: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300", label: "Easy", icon: CircleDot },
  moderate: { color: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300", label: "Moderate", icon: CircleAlert },
  challenging: { color: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300", label: "Challenging", icon: TriangleAlert },
  expert: { color: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300", label: "Expert", icon: OctagonAlert },
};
